import React, { useState, useRef, useCallback, useEffect, useReducer } from 'react'
import styled from 'styled-components'
import { Button, CodeSnippet as PrestyledCodeSnippet } from '@streamr/streamr-layout'
import { TABLET, DESKTOP, SANS, SEMIBOLD, MEDIUM } from '@streamr/streamr-layout/styled'
import { DiveIn } from './index.styled'
import app from './assets/app.png'
import app2x from './assets/app@2x.png'
import node from './assets/node.png'
import node2x from './assets/node@2x.png'
import lightNode from './assets/light-node.png'
import lightNode2x from './assets/light-node@2x.png'
import Container from '~shared/Container'
import ButtonWrapper from '~shared/ButtonWrapper'
import useCopy from '~hooks/useCopy'
import urls from '~utils/urls'

const WhiteBox = styled.div`
    background-color: #FFFFFF;
    border-radius: 8px;
    position: relative;
`

const SnippetBox = styled(WhiteBox)`
    button {
        position: absolute;
        bottom: 0.5rem;
        right: 0.5rem;
        appearance: none;
        background-color: #EFEFEF;
        color: #323232;
        margin: 0;
        border: 0;
        border-radius: 4px;
        font-size: 0.625rem;
        text-transform: uppercase;
        font-weight: ${MEDIUM};
        letter-spacing: 0.05rem;
        padding: 4px 8px;
        opacity: 0;
        transition: 300ms opacity ease-in-out;
        z-index: 2;

        :hover {
            background-color: #E7E7E7;
        }

        :active,
        :focus {
            background-color: #D8D8D8;
        }
    }

    :hover,
    :active,
    :focus-within {
        button {
            opacity: 1;
        }
    }
`

const CodeSamplesContainer = styled.div`
    display: flex;
    flex-direction: column;

    > * {
        flex: 1;
        max-height: 328px;
    }

    > * + * {
        margin-top: 1.5rem;
    }

    @media (min-width: ${TABLET}px) {
        > * {
            max-height: 480px;
        }

        > * + * {
            margin-top: 3.5rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        flex-direction: row;

        > * + * {
            margin-top: 0;
            margin-left: 1.5rem;
        }
    }
`

const CodeSnippet = styled(PrestyledCodeSnippet)`
    padding-left: 0;
    letter-spacing: 0.02rem;
    max-height: 328px;

    code:first-child {
        min-width: 48px;
        padding-right: 0 !important;
        margin-right: 24px;
        text-align: center;
        font-size: 0.625rem;
        line-height: 1.25rem;
        color: #ADADAD;
        position: relative;

        ::after {
            content: '';
            position: absolute;
            right: 0;
            top: -24px;
            bottom: -24px;
            background-color: #F5F5F5;
            width: 1px;
        }
    }

    code:last-child {
        font-size: 0.625rem;
        line-height: 1.25rem;
        display: block;
    }

    @media (min-width: ${TABLET}px) {
        max-height: 480px;

        code:first-child {
            min-width: 64px;
            margin-right: 42px;
            font-size: 0.75rem;
            line-height: 2rem;
        }

        code:last-child {
            font-size: 0.875rem;
            line-height: 2rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        code:first-child {
            margin-right: 28px;
        }
    }
`

const UnstyledTab = ({ children, ...props }) => (
    <div {...props}>
        <button type="button">{children || null}</button>
    </div>
)

const Tab = styled(UnstyledTab)`
    position: relative;

    button {
        cursor: pointer;
        appearance: none;
        background: transparent;
        border: 0;
        padding: 0;
        margin: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 64px;
        color: #E7E7E7;
        letter-spacing: 0.1rem;
        font-family: ${SANS};
        font-weight: ${SEMIBOLD};
        font-size: 0.625rem;
        line-height: 1rem;
        white-space: nowrap;
        transition: 300ms color ease-in-out;
    }

    :hover {
        button {
            color: #525252;
        }
    }

    :active,
    &[data-active="true"] {
        button {
            color: #0324FF;
        }
    }

    @media (min-width: ${TABLET}px) {
        button {
            font-size: 0.875rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
    }
`

const Tabs = styled.div`
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #F5F5F5;
    padding: 0 2rem;

    @media (min-width: ${TABLET}px) {
        padding: 0 6rem;
    }

    @media (min-width: ${DESKTOP}px) {
        padding: 0 4rem;
    }
`

const Icon = styled.div`
    img {
        width: 62px;
    }

    @media (min-width: ${TABLET}px) {
        img {
            width: 124px;
        }
    }
`

const ConnectMqqtIcon = () => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="46" height="46" rx="3" fill="#FAFAFA" stroke="#0324FF" strokeWidth="2" />
        <path
            // eslint-disable-next-line max-len
            d="M23.978 35.142c-.42-5.948-5.172-10.7-11.12-11.12M29.991 35.142c-.434-9.265-7.868-16.699-17.133-17.133M36 35.142C35.56 22.562 25.44 12.442 12.858 12"
            stroke="#0324FF"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <path
            d="M18.79 35.003a6.866 6.866 0 00-5.793-5.792c-.547-.08-.997.375-.997.928V35a1 1 0 001 1h4.861c.553 0 1.008-.45.929-.997z"
            fill="#0324FF"
        />
    </svg>
)

const ConnectHttpIcon = () => (
    <svg viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            // eslint-disable-next-line max-len
            d="M26.5 2.02a3 3 0 013 0l20.249 11.691a3 3 0 011.5 2.598v23.382a3 3 0 01-1.5 2.598L29.5 53.979a3 3 0 01-3 0L6.251 42.29a3 3 0 01-1.5-2.598V16.309a3 3 0 011.5-2.598L26.5 2.021z"
            fill="#fff"
            stroke="#0324FF"
            strokeWidth="2"
        />
        <path
            // eslint-disable-next-line max-len
            d="M28.75 30.5a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5zM36.25 21.5a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5zM36.25 34.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5zM28.75 39.5a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5zM18.25 39.5a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5zM19 23.75a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5zM31.4 24.1l3.26-3.26M32.044 28.544l2.285 2.285M28.75 30.5V35M26.101 29.399l-6.26 6.26M25.25 25.401l-4.39-2.635"
            stroke="#0324FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const ConnectWebsocketIcon = () => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="23" fill="#fff" stroke="#0324FF" strokeWidth="2" />
        <path
            // eslint-disable-next-line max-len
            d="M30.041 28.552h2.98v-7.15l-3.357-3.354-2.107 2.106 2.484 2.482v5.916zm2.988 1.492H22.636l-2.484-2.482 1.053-1.053 2.052 2.05h4.221l-4.158-4.163 1.061-1.06 4.159 4.155v-4.219l-2.044-2.042 1.045-1.045L22.377 15H12l2.972 2.97v.007h6.163l2.177 2.177-3.184 3.181-2.177-2.176V19.47h-2.98v2.922l5.157 5.154-2.099 2.098 3.357 3.354H36l-2.971-2.954z"
            fill="#0324FF"
        />
    </svg>
)

const LeftArrowIcon = () => (
    <svg width="206" height="8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="4" cy="4" r="4" fill="#0324FF" />
        <path d="M12 4h190" stroke="#0324FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="0.1 6" />
    </svg>
)

const UnstyledLeftArrow = (props) => (
    <div {...props}>
        <LeftArrowIcon />
    </div>
)

const LeftArrow = styled(UnstyledLeftArrow)`
    position: relative;
    overflow: hidden;
    height: 12px;

    svg {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
    }
`

const RightArrowIcon = () => (
    <svg width="208" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M208.288 5.102a1 1 0 010 1.796l-7.318 3.591a1 1 0 01-1.44-.898V2.41a1 1 0 011.44-.898l7.318 3.591z" fill="#0324FF" />
        <path d="M2 6h190" stroke="#0324FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="0.1 6" />
    </svg>
)

const UnstyledRightArrow = (props) => (
    <div {...props}>
        <RightArrowIcon />
    </div>
)

const RightArrow = styled(UnstyledRightArrow)`
    position: relative;
    overflow: hidden;
    height: 12px;

    svg {
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
    }
`

const MiddleIcon = styled.div`
    width: 40px;
    height: 40px;

    svg {
        width: 100%;
    }

    @media (min-width: ${TABLET}px) {
        width: 48px;
        height: 48px;
    }
`

const Connector = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin: 0 0.5rem;

    ${LeftArrow},
    ${RightArrow} {
        flex: 1;
    }

    ${MiddleIcon} {
        margin: 4px;
    }

    @media (min-width: ${TABLET}px) {
        margin: 0 1rem;

        ${MiddleIcon} {
            margin: 6px;
        }
    }
`

const Figure = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
`

const Caption = styled.div`
    text-align: center;
    font-size: 0.875rem;
    line-height: 2rem;
    color: #A3A3A3;
    font-style: italic;

    @media (min-width: ${DESKTOP}px) {
        font-size: 1.125rem;
    }
`

const Slide = styled.div`
    padding: 4.5rem 1.5rem 2rem;

    ${Figure} {
        margin: 0 1rem;
    }

    ${Figure} + ${Caption} {
        margin-top: 2.75rem;
    }

    @media (min-width: ${TABLET}px) {
        padding: 8.5rem 5.75rem 3rem;

        ${Figure} {
            margin: 0;
        }

        ${Figure} + ${Caption} {
            margin-top: 4rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        padding: 8rem 3.75rem 3.75rem;
    }
`

const Slides = styled.div`
    display: grid;
    grid-template-columns: 1fr;

    > * {
        grid-column: 1;
        grid-row: 1;
        opacity: 0;
        user-select: none;

        &[data-active="true"] {
            opacity: 1;
            z-index: 1;
            user-select: auto;
        }
    }

    &[data-animate="true"] > * {
        transition: 300ms opacity ease-in-out;
    }
`

const Nav = styled.div`
    position: relative;
`

const SliderTrack = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    opacity: 0;
    transition: opacity 180ms ease-in 600ms, left 300ms ease-in-out;
`

const Slider = styled.div`
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    background-color: #0324FF;
    width: 44px;
    height: 2px;
    border-radius: 1px;
`

const GenericSlide = ({ icon, children, ...props }) => (
    <Slide {...props}>
        <Figure>
            <Icon>
                <img src={app} srcSet={app2x} alt="" />
            </Icon>
            <Connector>
                <LeftArrow />
                <MiddleIcon>
                    {icon}
                </MiddleIcon>
                <RightArrow />
            </Connector>
            <Icon>
                <img src={node} srcSet={node2x} alt="" />
            </Icon>
        </Figure>
        <Caption>{children}</Caption>
    </Slide>
)

const MqqtSlide = (props) => (
    <GenericSlide icon={<ConnectMqqtIcon />} {...props}>
        Application connects over MQTT to a node
    </GenericSlide>
)

const UnstyledHttpSlide = (props) => (
    <GenericSlide icon={<ConnectHttpIcon />} {...props}>
        Application connects over HTTP to a node
    </GenericSlide>
)

const HttpSlide = styled(UnstyledHttpSlide)`
    ${MiddleIcon} {
        width: 46px;
        height: 46px;
        margin: 2px;

        @media (min-width: ${TABLET}px) {
            width: 56px;
            height: 56px;
        }
    }
`

const WebsocketSlide = (props) => (
    <GenericSlide icon={<ConnectWebsocketIcon />} {...props}>
        Application connects over Websocket to a node
    </GenericSlide>
)

const LightNodeSlide = (props) => (
    <Slide {...props}>
        <Figure>
            <Icon>
                <img src={lightNode} srcSet={lightNode2x} alt="" />
            </Icon>
        </Figure>
        <Caption>JS application imports a light node as a library</Caption>
    </Slide>
)

const UnstyledConnectToStreamr = (props) => {
    const codeRef = useRef({})
    const tabsRef = useRef(undefined)
    const [{ left, width, opacity }, updateSlider] = useReducer((prevState, nextState) => ({
        ...(prevState || {}),
        ...nextState,
    }), {
        left: 0,
        width: 0,
        opacity: 0,
    })

    const { copy, copied } = useCopy()

    const [snippet, setSnippet] = useState('lightNode')

    const copySnippet = useCallback(() => {
        copy(codeRef.current[snippet])
    }, [snippet, copy])

    const reposition = useCallback(() => {
        if (tabsRef.current) {
            const activeNode = Array.from(tabsRef.current.children).find((n) => n.getAttribute('data-active') === 'true')

            if (activeNode) {
                updateSlider({
                    left: activeNode.offsetLeft,
                    width: activeNode.offsetWidth,
                    opacity: 1,
                })
            }
        }
    }, [])

    useEffect(() => {
        reposition()
    }, [snippet, reposition])

    useEffect(() => {
        window.addEventListener('resize', reposition)
        reposition()

        return () => {
            window.removeEventListener('resize', reposition)
        }
    }, [reposition])

    return (
        <Container {...props}>
            <CodeSamplesContainer $appearIntersection>
                <WhiteBox $appearDirection="rightwards" $appearImmediate>
                    <Nav>
                        <Tabs ref={tabsRef}>
                            <Tab data-active={snippet === 'lightNode'} onClick={() => setSnippet('lightNode')}>LIGHT NODE (JS)</Tab>
                            <Tab data-active={snippet === 'websocket'} onClick={() => setSnippet('websocket')}>WEBSOCKET</Tab>
                            <Tab data-active={snippet === 'http'} onClick={() => setSnippet('http')}>HTTP</Tab>
                            <Tab data-active={snippet === 'mqtt'} onClick={() => setSnippet('mqtt')}>MQTT</Tab>
                        </Tabs>
                        <SliderTrack style={{
                            left,
                            width,
                            opacity,
                        }}
                        >
                            <Slider />
                        </SliderTrack>
                    </Nav>
                    <Slides data-animate="true">
                        <MqqtSlide data-active={snippet === 'mqtt'} />
                        <HttpSlide data-active={snippet === 'http'} />
                        <WebsocketSlide data-active={snippet === 'websocket'} />
                        <LightNodeSlide data-active={snippet === 'lightNode'} />
                    </Slides>
                </WhiteBox>
                <SnippetBox $appearDirection="leftwards" $appearImmediate>
                    <Slides>
                        <CodeSnippet
                            id="mqtt"
                            language="javascript"
                            data-active={snippet === 'mqtt'}
                            codeRef={codeRef}
                        >
                            {`
                                // Use your favourite language and MQTT library!

                                // Connect to MQTT plugin on your Streamr node
                                mqtt.connect('mqtt://my-streamr-node')

                                // Publish a message to a stream
                                mqtt.publish('mydomain.eth/test', {
                                    hello: 'world',
                                })

                                // Or subscribe to a stream of messages
                                mqtt.subscribe('mydomain.eth/test', (msg) => {
                                    // Handle incoming messages
                                })
                            `}
                        </CodeSnippet>
                        <CodeSnippet
                            id="http"
                            language="javascript"
                            data-active={snippet === 'http'}
                            codeRef={codeRef}
                        >
                            {`
                                // Use your favourite language and HTTP library!

                                // You'll want to URI-encode the stream id
                                const streamId = encodeURIComponent('mydomain.eth/test')

                                // Publish messages to a stream by POSTing JSON 
                                // to the HTTP plugin on your Streamr node
                                http.post(\`http://my-streamr-node:7171/streams/$\{streamId}\`, {
                                    hello: 'world'
                                })
                            `}
                        </CodeSnippet>
                        <CodeSnippet
                            id="websocket"
                            language="javascript"
                            data-active={snippet === 'websocket'}
                            codeRef={codeRef}
                        >
                            {`
                                // Use your favourite language & Websocket library

                                // You'll want to URI-encode the stream id
                                const streamId = encodeURIComponent('mydomain.eth/test')

                                // Connect to the Websocket plugin on your Streamr 
                                // node and send JSON messages to publish them
                                const pub = ws.connect(\`ws://my-streamr-node:7170/streams/$\{streamId}/publish\`)
                                pub.send({
                                    hello: 'world',
                                })

                                // Or subscribe to a stream of messages
                                const sub = ws.connect(\`ws://my-streamr-node:7170/streams/$\{streamId}/subscribe\`)
                                sub.onmessage = (msg) => {
                                    // Handle incoming messages
                                }
                            `}
                        </CodeSnippet>
                        <CodeSnippet
                            id="lightNode"
                            language="javascript"
                            data-active={snippet === 'lightNode'}
                            codeRef={codeRef}
                        >
                            {`
                                // Run a Streamr node right inside your JS app
                                const StreamrClient = require('streamr-client')
                                const streamr = new StreamrClient({
                                    auth: {
                                        privateKey: 'ethereum-private-key'
                                    }
                                })

                                // Publish messages to a stream
                                streamr.publish('mydomain.eth/test', {
                                    hello: 'world',
                                })

                                // Or subscribe to a stream of messages
                                streamr.subscribe('mydomain.eth/test', (msg) => {
                                    // Handle incoming messages
                                })
                            `}
                        </CodeSnippet>
                    </Slides>
                    <button
                        type="button"
                        onClick={copySnippet}
                    >
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                </SnippetBox>
            </CodeSamplesContainer>
            <DiveIn>
                <div>
                    <h3 $appear>Dive into the Streamr docs to learn more</h3>
                    <ButtonWrapper>
                        <Button
                            kind="primary"
                            size="big"
                            href={urls.docs.gettingStarted}
                            tag="a"
                        >
                            Get started now
                        </Button>
                    </ButtonWrapper>
                </div>
            </DiveIn>
        </Container>
    )
}

const ConnectToStreamr = styled(UnstyledConnectToStreamr)`
    padding: 0 1rem;

    @media (min-width: ${TABLET}px) {
        padding: 0;
        max-width: 44rem;
    }

    @media (min-width: ${DESKTOP}px) {
        max-width: 72rem;
    }
`

export default ConnectToStreamr
